import { useEffect, useState } from "react";
import { Plus, Trash2, Shield } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ImageUpload from "@/components/ImageUpload";

const CrestManager = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const field =
    "w-full rounded-xl border border-edge bg-ink2 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-crimson focus:outline-none";

  useEffect(() => {
    API.get("/crests").then(({ data }) => setRows(data.crests || [])).catch(() => {});
  }, []);

  const update = (i, patch) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await API.put("/admin/crests", { crests: rows });
      setRows(rows.filter((r) => r.name?.trim() && r.url?.trim()));
      toast.success(`${data.count} escudo(s) guardado(s). Ya se ven en crónicas y portada.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-edge bg-ink2 p-6" data-testid="crest-manager">
      <p className="mb-2 flex items-center gap-2 font-display text-lg font-bold uppercase text-white">
        <Shield className="h-4 w-4 text-crimson" /> Escudos de rivales
      </p>
      <p className="mb-5 text-sm leading-relaxed text-slate-400">
        Sube el escudo de cada equipo escribiendo su nombre{" "}
        <span className="font-bold text-white">exactamente igual</span> que en tus crónicas y en la
        franja de portada (ej.: Racing de Santander). Se mostrarán en los marcadores.
      </p>

      <div className="space-y-3">
        {rows.map((row, i) => (
          <div key={i} className="flex flex-wrap items-center gap-3" data-testid={`crest-row-${i}`}>
            {row.url ? (
              <img src={row.url} alt={row.name} className="h-11 w-11 shrink-0 rounded-lg border border-edge bg-ink object-contain p-1" />
            ) : (
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-dashed border-edge font-mono-score text-[9px] text-slate-500">
                ?
              </div>
            )}
            <input
              data-testid={`crest-name-${i}`}
              value={row.name}
              onChange={(e) => update(i, { name: e.target.value })}
              placeholder="Nombre del equipo"
              className={field + " min-w-[180px] flex-1"}
            />
            <ImageUpload onUploaded={(url) => update(i, { url })} />
            <button
              data-testid={`crest-delete-${i}`}
              onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-edge text-slate-400 transition-colors hover:border-crimson hover:text-crimson"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <Button
          data-testid="crest-add"
          onClick={() => setRows((rs) => [...rs, { name: "", url: "" }])}
          className="rounded-full border border-edge bg-transparent font-display font-bold uppercase tracking-wider text-slate-300 hover:text-white"
        >
          <Plus className="h-4 w-4" /> Añadir equipo
        </Button>
        <Button
          data-testid="crest-save"
          onClick={save}
          disabled={busy}
          className="rounded-full bg-crimson px-6 font-display font-bold uppercase tracking-wider hover:bg-glow"
        >
          {busy ? "Guardando..." : "Guardar escudos"}
        </Button>
      </div>
    </div>
  );
};

export default CrestManager;
